/**
 * Every failed API call rejects with an ApiError (port of hp-app-admin-react).
 *
 * sreg-api error bodies:
 *   handler errors     { message: string,   correlationId, error?: { code, params } }
 *   middleware errors  { message: string[], correlationId, error?: { code, params } }
 *   BFF (FastAPI)      { detail: string }
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId?: string;
  readonly userMessage: string;
  readonly endpoint: string;
  readonly method: string;

  constructor(init: {
    status: number;
    code: string;
    correlationId?: string;
    userMessage: string;
    endpoint: string;
    method: string;
    rawBody: string;
  }) {
    super(`API Error ${init.status}: ${init.rawBody}`);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.correlationId = init.correlationId;
    this.userMessage = init.userMessage;
    this.endpoint = init.endpoint;
    this.method = init.method;
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

export interface ParsedErrorBody {
  code: string;
  correlationId?: string;
  prose?: string;
}

/** Normalises the error body shapes. Never throws. */
export function parseErrorBody(status: number, rawBody: string): ParsedErrorBody {
  let body: Record<string, unknown>;
  try {
    const json = JSON.parse(rawBody);
    if (!json || typeof json !== 'object') return { code: 'UNKNOWN' };
    body = json as Record<string, unknown>;
  } catch {
    return { code: status === 501 ? 'NOT_IMPLEMENTED' : 'UNKNOWN', prose: rawBody || undefined };
  }
  const err = body.error && typeof body.error === 'object' ? (body.error as Record<string, unknown>) : undefined;
  const message = Array.isArray(body.message) ? body.message.join('; ') : body.message;
  const prose = typeof message === 'string' ? message : typeof body.detail === 'string' ? body.detail : undefined;
  return {
    code: typeof err?.code === 'string' ? err.code : 'UNKNOWN',
    correlationId: typeof body.correlationId === 'string' ? body.correlationId : undefined,
    prose,
  };
}

const STATUS_MESSAGES: Record<number, string> = {
  400: 'That request was not valid.',
  401: 'Please sign in again.',
  403: "You don't have access to that.",
  404: 'That item could not be found.',
  409: 'That changed in the meantime — refresh and try again.',
  502: 'Simon could not reach a service it depends on. Try again shortly.',
  503: 'Simon is temporarily unavailable.',
};

/** What the UI shows: the server's message when it is meant for people, else a status default. */
export function userMessageFor(status: number, parsed: ParsedErrorBody): string {
  if (status < 500 && parsed.prose && parsed.prose.length < 200) return parsed.prose;
  return STATUS_MESSAGES[status] ?? 'Something went wrong. Try again.';
}
